import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ColDef } from 'ag-grid-community';
import { VendorService } from '@app/shared/services/vendor.service';
import {
  OpsQuarterlyFeedbackItem,
  OpsQuarterlyFeedbackRow,
} from '@app/shared/interfaces/contractor-profile';
import { ToasterService } from '@app/shared/services/toaster.service';
import {
  QUARTERLY_FEEDBACK_COLUMNS,
  QUARTERLY_FEEDBACK_TABLE_CONFIG,
} from './quaterly-feedback.columns';
import { TableConfig } from '@shared/components/table/table.interfaces';
import { ExportService } from '@app/shared/services/export.service';

@Component({
  selector: 'app-quaterly-feedback',
  templateUrl: './quaterly-feedback.component.html',
  styleUrl: './quaterly-feedback.component.scss',
})
export class QuaterlyFeedbackComponent implements OnInit {
  // 'ops' → no employeeId sent
  // 'hiring-manager' → employeeId required (pass via @Input)
  @Input() persona: 'ops' | 'vendor-management' | 'hiring-manager' = 'ops';
  @Input() employeeId?: string;

  useCaseKey = 'opsVendorManagement';
  flowKey = 'quarterlyFeedback';

  selectedYear: string = String(new Date().getFullYear());
  selectedQuarter: string = (() => {
    const month = new Date().getMonth() + 1; // 1–12
    if (month <= 3) return 'Q1';
    if (month <= 6) return 'Q2';
    if (month <= 9) return 'Q3';
    return 'Q4';
  })();
  isLoading = false;

  columnDefs: ColDef[] = QUARTERLY_FEEDBACK_COLUMNS;
  tableConfig: TableConfig = QUARTERLY_FEEDBACK_TABLE_CONFIG;
  rowData: OpsQuarterlyFeedbackRow[] = [];

  gridOptions = {
    context: { componentParent: this },
  };

  yearOptions = (() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => ({
      label: String(currentYear - i),
      value: String(currentYear - i),
    }));
  })();

  quarterOptions = [
    { label: 'Q1 (Jan - Mar)', value: 'Q1' },
    { label: 'Q2 (Apr - Jun)', value: 'Q2' },
    { label: 'Q3 (Jul - Sep)', value: 'Q3' },
    { label: 'Q4 (Oct - Dec)', value: 'Q4' },
  ];

  constructor(
    private router: Router,
    private vendorService: VendorService,
    private toasterService: ToasterService,
    private exportService: ExportService,
  ) {}

  ngOnInit(): void {
    if (this.router.url.startsWith('/vendor-management')) {
      this.useCaseKey = 'vendorManagement';
      this.persona = 'vendor-management';
      const userProfile = sessionStorage.getItem('userProfile');
      this.employeeId = userProfile ? JSON.parse(userProfile)?.employeeId : undefined;
    } else if (this.router.url.startsWith('/hiring-manager')) {
      this.useCaseKey = 'opsVendorManagement';
      this.persona = 'hiring-manager';
      // employeeId must be passed via @Input() from the hiring-manager context
    } else {
      this.useCaseKey = 'opsVendorManagement';
      this.persona = 'ops';
    }
    this.loadFeedback();
  }

  // One row per employee — comments joined as numbered multiline string
  private mapData(items: OpsQuarterlyFeedbackItem[]): OpsQuarterlyFeedbackRow[] {
    return items.map((item) => ({
      employeeId: item.employeeId,
      employeeName: item.employeeName,
      reportingManagerName: item.reportingManagerName,
      lastHireDate: item.lastHireDate,
      employerName: item.employerName,
      l3Name: item.l3Name,
      technicalRating: item.technicalRating,
      behaviouralRating: item.behaviouralRating,
      performanceRating: item.performanceRating ?? null,
      comments: item.comments?.length
        ? item.comments.length === 1
          ? item.comments[0]
          : item.comments.map((c, i) => `${i + 1}. ${c}`).join('\n')
        : '',
      contractorFeedbackQuarterId: item.contractorFeedbackQuarterId,
      contractorFeedbackYearId: item.contractorFeedbackYearId,
    }));
  }

  loadFeedback(): void {
    if (!this.selectedQuarter || !this.selectedYear) return;

    this.isLoading = true;
    this.rowData = [];

    const empId = (this.persona === 'hiring-manager' || this.persona === 'vendor-management') ? this.employeeId : undefined;

    this.vendorService
      .getOpsQuarterlyFeedback(this.selectedQuarter, this.selectedYear, empId)
      .subscribe({
        next: (response) => {
          this.rowData = this.mapData(response?.data ?? []);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  onYearChange(event: any): void {
    this.selectedYear = event ?? this.selectedYear;
    this.loadFeedback();
  }

  onQuarterChange(event: any): void {
    this.selectedQuarter = event ?? this.selectedQuarter;
    this.loadFeedback();
  }

  onDownload(): void {
  const exportColumns = [
    { field: 'employeeId',           headerName: 'Employee ID' },
    { field: 'employeeName',         headerName: 'Employee Name' },
    { field: 'reportingManagerName', headerName: 'Reporting Manager' },
    { field: 'lastHireDate',         headerName: 'Last Hire Date' },
    { field: 'employerName',         headerName: 'Vendor' },
    { field: 'l3Name',               headerName: 'L3 Manager' },
    { field: 'performanceRating',    headerName: 'Performance Rating' },
    { field: 'technicalRating',      headerName: 'Technical Rating' },
    { field: 'behaviouralRating',    headerName: 'Behavioural Rating' },
    { field: 'comments',             headerName: 'Comments' },
  ];
  const exportData = this.rowData.map(row => ({
    ...row,
    comments: (() => {
      const parts = row.comments
        ? row.comments.split('\n').filter(c => c.trim())
        : [];
      if (parts.length === 0) return '';
      if (parts.length === 1) return parts[0].replace(/^\d+\.\s*/, ''); // strip "1. "
      return parts.join('    ');                                        // "1. x  |  2. y"
    })(),
  }));

  const fileName = `quarterly-feedback-${this.selectedQuarter}-${this.selectedYear}`;
  this.exportService.exportToExcel(exportData, exportColumns, fileName);
}
}